"use client";

import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { LEDGER_CURRENCY_OPTIONS } from "@/components/manage-workspace-modal/sections/manage-ledgers/ledger-currencies";

export interface CurrencySelectorProps {
  value: string;
  onValueChange: (currency: string) => void;
  id?: string;
  label?: string;
  placeholder?: string;
  /** Extra ISO codes to include (e.g. brand home currency). */
  extraCurrencies?: string[];
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  hideLabel?: boolean;
}

export function CurrencySelector({
  value,
  onValueChange,
  id,
  label = "Currency",
  placeholder = "Select currency",
  extraCurrencies = [],
  disabled,
  className,
  triggerClassName,
  hideLabel = false,
}: CurrencySelectorProps) {
  const [open, setOpen] = useState(false);

  const options = useMemo(() => {
    const codes = new Set<string>(LEDGER_CURRENCY_OPTIONS);
    for (const code of extraCurrencies) {
      const normalized = code.trim().toUpperCase();
      if (normalized) codes.add(normalized);
    }
    const current = value.trim().toUpperCase();
    if (current) codes.add(current);
    return [...codes].sort();
  }, [extraCurrencies, value]);

  const displayValue = value.trim().toUpperCase();

  return (
    <div className={cn("grid gap-2", className)}>
      {!hideLabel && label ? <Label htmlFor={id}>{label}</Label> : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between font-normal", triggerClassName)}
            disabled={disabled}
          >
            {displayValue ? (
              <span>{displayValue}</span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command>
            <CommandInput placeholder="Search currencies..." />
            <CommandList>
              <CommandEmpty>No currency found.</CommandEmpty>
              <CommandGroup>
                {options.map((currency) => (
                  <CommandItem
                    key={currency}
                    value={currency}
                    onSelect={() => {
                      onValueChange(currency);
                      setOpen(false);
                    }}
                  >
                    {currency}
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        displayValue === currency ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
